import viewProductVariants from '@/shared/api/product/viewProductVariants';
import getProducts from '@/shared/api/products/getProducts';
import Product from '@/shared/types/product.type';
import removeDuplicatesArray from '@/shared/utils/removeDublicatesArray';

import getSearchOverFields from '../utils/getSearchOverFields';
import getSearchOverValues from '../utils/getSearchOverValues';

const createSearchBody = (
  searchInputValue: string,
  filterBy?: string[],
  cop?: string[],
) => {
  return getSearchOverFields(
    searchInputValue,
    filterBy || [
      'product_category',
      'product_description',
      'product_id',
      'variant_id',
      'product_name',
      'variant_name',
      'bar_code',
      'ts_created',
      'value',
      'sku',
    ],
    cop,
  );
};

const processVariants = async (
  variantsResponseData: Record<string, unknown>[],
  redirectLogin: () => void,
) => {
  if (variantsResponseData.length > 0) {
    const productIds = variantsResponseData
      .filter((item) => !item.value)
      .map((item) => ({ product_id: item.product_id })) as Product[];

    const noDuplicatesIds = removeDuplicatesArray(productIds, 'product_id').map(
      (item) => item.product_id,
    );

    const searchBody = getSearchOverValues({
      key: 'product_id',
      searchValues: noDuplicatesIds,
      cop: ['='],
      op: 'or',
    });

    const responseProducts = await getProducts(
      { search: searchBody },
      redirectLogin,
    );

    if (responseProducts.success) {
      return variantsResponseData.map((item) => {
        const parentProduct = responseProducts.data.find(
          (product) => product.product_id === item.product_id,
        ) as Product;

        if (parentProduct) {
          return {
            ...item,
            value: item.value || parentProduct.value,
            product_name: parentProduct.product_name,
          };
        }

        return item;
      });
    }
  }

  return variantsResponseData;
};

type Args = {
  searchInputValue: string;
  source?: 'input' | 'barcodeScanner';
  filterBy?: string[];
  cop?: string[];
  redirectLogin: () => void;
};

const getSearchData = async ({
  searchInputValue,
  source = 'input',
  filterBy,
  cop,
  redirectLogin,
}: Args) => {
  let products: Record<string, unknown>[] | null = null;
  let variants: Record<string, unknown>[] | null = null;

  const fetchProducts = async () => {
    const productsResp = await getProducts(
      {
        search: createSearchBody(searchInputValue, filterBy, cop),
        page_size: 10,
      },
      redirectLogin,
    );

    if (productsResp.success) {
      return productsResp.data;
    }

    return null;
  };

  const fetchVariants = async () => {
    const response = await viewProductVariants({
      body: {
        search: createSearchBody(searchInputValue, filterBy, cop),
        page_size: 10,
      },
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      const variants = response.data as Record<string, unknown>[];
      const updatedVariants = await processVariants(variants, redirectLogin);

      return updatedVariants;
    }

    return null;
  };

  if (source === 'barcodeScanner') {
    if (searchInputValue) {
      products = await fetchProducts();
      variants = await fetchVariants();
    }
  } else {
    products = await fetchProducts();
    variants = await fetchVariants();
  }

  return {
    products,
    variants,
  };
};

export default getSearchData;
